import { useMemo } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  ChakraSpacer,
  ChakraTag,
  ChakraTagCloseButton,
  ChakraTagLabel,
  ChakraText,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  IconButton,
  Reset,
} from '@equip.health/ui';

import { PatientNoteFilterType } from '~/lib/constants/AddPatientNoteModal.constants';
import { getNotesSelectedFilter } from '~/lib/util/notes/notes.util';

const { labelBold } = EquipUITheme.typography;
const { primary } = EquipUIFireflyV2Theme.colors;

const getTagLabel = ({ displayText }: NoteFilterOptionWithKeyType): string => {
  return displayText;
};

type PatientNotesSelectedFiltersProps = {
  filters: PatientNoteFilterType;
  handleRemove: (option: any) => void;
  handleReset: () => void;
};

const TAG_HEIGHT = 25;
const TAG_VERTICAL_MARGIN = 4;

const TOP_LEVEL_ID = 'patient-notes-selected-filter';

const PatientNotesSelectedFilters = ({
  filters,
  handleRemove,
  handleReset,
}: PatientNotesSelectedFiltersProps) => {
  const optionsList = useMemo<NoteFilterOptionWithKeyType[]>(
    () => getNotesSelectedFilter(filters),
    [filters],
  );
  return (
    optionsList?.length > 0 && (
      <ChakraFlex alignItems="center" marginTop="24px">
        <ChakraText {...labelBold} id={`${TOP_LEVEL_ID}__filters-label`}>
          Filters
        </ChakraText>
        <ChakraSpacer />
        <ChakraHStack
          alignItems="center"
          display="flex"
          flexWrap="wrap"
          spacing={4}
        >
          {optionsList.map((option: NoteFilterOptionWithKeyType) => {
            const { keyName, value } = option;
            const id = `${keyName}::tag::${value}`;
            return (
              <ChakraTag
                bgColor="secondary.50"
                borderRadius="8px"
                color="neutral.background.primary"
                data-testid={id}
                height={`${TAG_HEIGHT}px`}
                id={`${TOP_LEVEL_ID}__${keyName}-${value}`}
                key={id}
                margin={`${TAG_VERTICAL_MARGIN}px 0 ${TAG_VERTICAL_MARGIN}px 8px !important`}
                size="md"
                variant="solid"
              >
                <ChakraTagLabel
                  {...labelBold}
                  data-testid={`taglabel::${value}`}
                >
                  {getTagLabel(option)}
                </ChakraTagLabel>
                <ChakraTagCloseButton
                  fontSize="24px"
                  onClick={() => handleRemove(option)}
                />
              </ChakraTag>
            );
          })}
        </ChakraHStack>
        <ChakraBox marginX="8px">
          <IconButton
            ariaLabel="reset"
            icon={<Reset fill={primary[100]} height="12px" width="12px" />}
            id={`${TOP_LEVEL_ID}__reset`}
            onClick={handleReset}
            size="xs"
            variant="outline"
          />
        </ChakraBox>
      </ChakraFlex>
    )
  );
};

export default PatientNotesSelectedFilters;
